import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileEdit, FileText, Image, Loader2, Plus, Save, Trash, Video } from 'lucide-react';
import { useAuth } from '@/contexts/auth/AuthContext';
import TaxonomyBadge from '@/components/learning/TaxonomyBadge';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePortfolio } from '@/hooks/usePortfolio';
import { ContentType, NewPortfolioItemFormData, PortfolioItem } from '@/services/portfolio/types';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

const defaultFormData: NewPortfolioItemFormData = {
  title: '',
  description: '',
  item_type: 'project',
  content_type: 'text',
  content: '',
  taxonomy_level: 'create',
};

const PortfolioGenerator = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('all');
  const [isCreating, setIsCreating] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [formData, setFormData] = useState<NewPortfolioItemFormData>(defaultFormData);
  
  const {
    items: portfolioItems,
    isLoading,
    isSubmitting,
    addPortfolioItem,
    removePortfolioItem,
  } = usePortfolio();

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateItem = async () => {
    if (!user) return;
    
    // Validate inputs
    if (!formData.title) {
      toast({
        title: "Missing title",
        description: "Please provide a title for your portfolio item.",
        variant: "destructive",
      });
      return;
    }
    
    // For content types that need text content
    if (formData.content_type === 'text' && !formData.content) {
      toast({
        title: "Missing content",
        description: "Please provide text content for this item.",
        variant: "destructive",
      });
      return;
    }
    
    // For content types that need a URL
    if ((formData.content_type === 'image' || formData.content_type === 'video') && !formData.media_url) {
      toast({
        title: "Missing URL",
        description: `Please provide a valid URL for your ${formData.content_type}.`,
        variant: "destructive",
      });
      return;
    }
    
    // If content type is video/image, also set that as the item_type for consistency
    let item_type = formData.item_type;
    if (formData.content_type === 'video' || formData.content_type === 'image') {
      item_type = formData.content_type;
    }
    
    const newItem = {
      ...formData,
      item_type,
    };
    
    const result = await addPortfolioItem(newItem);
    
    if (result) {
      // Reset form
      setFormData(defaultFormData);
      setIsCreating(false);
    }
  };

  const handleDeleteItem = async (id: string) => {
    setItemToDelete(id);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      await removePortfolioItem(itemToDelete);
      setItemToDelete(null);
    }
  };

  const cancelDelete = () => {
    setItemToDelete(null);
  };

  const getContentTypeIcon = (item_type: string) => {
    switch (item_type) {
      case 'project':
      case 'reflection':
      case 'analysis':
      case 'evaluation':
        return <FileText className="h-5 w-5 text-blue-500" />;
      case 'image':
        return <Image className="h-5 w-5 text-green-500" />;
      case 'video':
        return <Video className="h-5 w-5 text-red-500" />;
      default:
        return <FileText className="h-5 w-5 text-gray-500" />;
    }
  };

  const getTaxonomyColor = (level: string) => {
    switch (level) {
      case 'remember':
        return 'bg-slate-100 text-slate-700';
      case 'understand':
        return 'bg-blue-100 text-blue-700';
      case 'apply':
        return 'bg-green-100 text-green-700';
      case 'analyze':
        return 'bg-amber-100 text-amber-700';
      case 'evaluate':
        return 'bg-orange-100 text-orange-700';
      case 'create':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const hasVideoContent = (item: PortfolioItem) => {
    return item.item_type === 'video';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
        <div>
          <h1 className="text-2xl font-bold">Create Level Portfolio</h1>
          <p className="text-muted-foreground">Showcase your creative work at the highest taxonomy level</p>
          <div className="mt-2">
            <TaxonomyBadge taxonomyLevel="create" />
          </div>
        </div>
        <Button
          onClick={() => setIsCreating(true)}
          className="mt-2 md:mt-0 bg-portfolio-primary hover:bg-portfolio-accent text-white"
        >
          <Plus className="mr-2 h-4 w-4" /> Add Portfolio Item
        </Button>
      </div>
      
      <Dialog open={isCreating} onOpenChange={setIsCreating}>
        <DialogContent className="max-w-md md:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-portfolio-text">Create New Portfolio Item</DialogTitle>
            <DialogDescription>Add a new artifact to your creative portfolio</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="title" className="text-sm font-medium">Title</label>
              <Input 
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Give your portfolio item a name"
              />
            </div>
            
            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium">Description</label>
              <Textarea 
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Briefly describe this portfolio item"
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Item Type</label>
              <Select 
                value={formData.item_type} 
                onValueChange={(value) => handleSelectChange('item_type', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select item type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="project">Project</SelectItem>
                  <SelectItem value="reflection">Reflection</SelectItem>
                  <SelectItem value="analysis">Analysis</SelectItem>
                  <SelectItem value="evaluation">Evaluation</SelectItem>
                  <SelectItem value="certificate">Certificate</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Content Type</label>
              <Tabs 
                defaultValue="text" 
                value={formData.content_type}
                onValueChange={(value) => handleSelectChange('content_type', value as ContentType)}
                className="w-full"
              >
                <TabsList className="grid grid-cols-3 mb-2">
                  <TabsTrigger value="text">Text</TabsTrigger>
                  <TabsTrigger value="image">Image</TabsTrigger>
                  <TabsTrigger value="video">Video</TabsTrigger>
                </TabsList>
                <TabsContent value="text" className="mt-0">
                  <Textarea
                    placeholder="Enter your content text here..."
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    rows={5}
                  />
                </TabsContent>
                <TabsContent value="image" className="mt-0">
                  <Input
                    type="url"
                    placeholder="Enter image URL"
                    name="media_url"
                    value={formData.media_url || ''}
                    onChange={handleInputChange}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    For demonstration purposes, please use direct URLs. Image upload will be implemented in the future.
                  </p>
                </TabsContent>
                <TabsContent value="video" className="mt-0">
                  <Input
                    type="url"
                    placeholder="Enter video URL (YouTube, Vimeo, etc.)"
                    name="media_url"
                    value={formData.media_url || ''}
                    onChange={handleInputChange}
                  />
                </TabsContent>
              </Tabs>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Taxonomy Level</label>
              <Select 
                value={formData.taxonomy_level} 
                onValueChange={(value) => handleSelectChange('taxonomy_level', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select taxonomy level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="remember">Remember</SelectItem>
                  <SelectItem value="understand">Understand</SelectItem>
                  <SelectItem value="apply">Apply</SelectItem>
                  <SelectItem value="analyze">Analyze</SelectItem>
                  <SelectItem value="evaluate">Evaluate</SelectItem>
                  <SelectItem value="create">Create</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreating(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateItem}
              disabled={isSubmitting}
              className="bg-portfolio-primary hover:bg-portfolio-accent text-white"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Item
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <AlertDialog open={!!itemToDelete} onOpenChange={(open) => !open && setItemToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this portfolio item. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All Items</TabsTrigger>
          <TabsTrigger value="project">Projects</TabsTrigger>
          <TabsTrigger value="reflection">Reflections</TabsTrigger>
          <TabsTrigger value="create">Create Level</TabsTrigger>
        </TabsList>
        
        <TabsContent value="all" className="mt-6">
          {isLoading ? (
            <div className="flex justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-portfolio-primary" />
            </div>
          ) : portfolioItems.length === 0 ? (
            <Card className="flex flex-col items-center justify-center py-12">
              <FileEdit className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No portfolio items yet</h3>
              <p className="text-muted-foreground text-center max-w-md mt-1 mb-4">
                Create your first portfolio item to showcase your creative work at the highest taxonomy level.
              </p>
              <Button onClick={() => setIsCreating(true)}>
                <Plus className="mr-2 h-4 w-4" /> Add Portfolio Item
              </Button>
            </Card>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {portfolioItems.map((item) => (
                <Card key={item.id} className="overflow-hidden transition-all hover:shadow-md">
                  {item.media_url && !hasVideoContent(item) && (
                    <div className="aspect-video w-full overflow-hidden bg-slate-100">
                      <img 
                        src={item.media_url} 
                        alt={item.title}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="truncate">{item.title}</CardTitle>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${getTaxonomyColor(item.taxonomy_level)}`}>
                          {item.taxonomy_level}
                        </span>
                        {getContentTypeIcon(item.item_type)}
                      </div>
                    </div>
                    <CardDescription>{item.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {item.content && (
                      <p className="line-clamp-3 text-sm">{item.content}</p>
                    )}
                    {item.media_url && hasVideoContent(item) && (
                      <div className="flex items-center text-sm text-blue-500 hover:underline">
                        <Video className="mr-2 h-4 w-4" />
                        <a href={item.media_url} target="_blank" rel="noopener noreferrer">
                          View video
                        </a>
                      </div>
                    )}
                  </CardContent>
                  <CardFooter className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">
                      Added on {new Date(item.created_at).toLocaleDateString()}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteItem(item.id)}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="project" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {portfolioItems
              .filter(item => item.item_type === 'project')
              .map((item) => (
                <Card key={item.id} className="overflow-hidden transition-all hover:shadow-md">
                  {item.media_url && item.item_type !== 'video' && (
                    <div className="aspect-video w-full overflow-hidden bg-slate-100">
                      <img 
                        src={item.media_url} 
                        alt={item.title}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="truncate">{item.title}</CardTitle>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${getTaxonomyColor(item.taxonomy_level)}`}>
                          {item.taxonomy_level}
                        </span>
                        {getContentTypeIcon(item.item_type)}
                      </div>
                    </div>
                    <CardDescription>{item.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {item.content && (
                      <p className="line-clamp-3 text-sm">{item.content}</p>
                    )}
                    {item.media_url && item.item_type === 'video' && (
                      <div className="flex items-center text-sm text-blue-500 hover:underline">
                        <Video className="mr-2 h-4 w-4" />
                        <a href={item.media_url} target="_blank" rel="noopener noreferrer">
                          View video
                        </a>
                      </div>
                    )}
                  </CardContent>
                  <CardFooter className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">
                      Added on {new Date(item.created_at).toLocaleDateString()}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteItem(item.id)}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </CardFooter>
                </Card>
              ))}
          </div>
        </TabsContent>

        <TabsContent value="reflection" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {portfolioItems
              .filter(item => item.item_type === 'reflection')
              .map((item) => (
                <Card key={item.id} className="overflow-hidden transition-all hover:shadow-md">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="truncate">{item.title}</CardTitle>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${getTaxonomyColor(item.taxonomy_level)}`}>
                          {item.taxonomy_level}
                        </span>
                        {getContentTypeIcon(item.item_type)}
                      </div>
                    </div>
                    <CardDescription>{item.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {item.content && (
                      <p className="line-clamp-3 text-sm">{item.content}</p>
                    )}
                  </CardContent>
                  <CardFooter className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">
                      Added on {new Date(item.created_at).toLocaleDateString()}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteItem(item.id)}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </CardFooter>
                </Card>
              ))}
          </div>
        </TabsContent>

        <TabsContent value="create" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {portfolioItems
              .filter(item => item.taxonomy_level === 'create')
              .map((item) => (
                <Card key={item.id} className="overflow-hidden transition-all hover:shadow-md">
                  {item.media_url && item.item_type !== 'video' && (
                    <div className="aspect-video w-full overflow-hidden bg-slate-100">
                      <img 
                        src={item.media_url} 
                        alt={item.title}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="truncate">{item.title}</CardTitle>
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700">
                          create
                        </span>
                        {getContentTypeIcon(item.item_type)}
                      </div>
                    </div>
                    <CardDescription>{item.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {item.content && (
                      <p className="line-clamp-3 text-sm">{item.content}</p>
                    )}
                    {item.media_url && item.item_type === 'video' && (
                      <div className="flex items-center text-sm text-blue-500 hover:underline">
                        <Video className="mr-2 h-4 w-4" />
                        <a href={item.media_url} target="_blank" rel="noopener noreferrer">
                          View video
                        </a>
                      </div>
                    )}
                  </CardContent>
                  <CardFooter className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">
                      Added on {new Date(item.created_at).toLocaleDateString()}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteItem(item.id)}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </CardFooter>
                </Card>
              ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PortfolioGenerator;
